import {
  Activity,
  BookOpenCheck,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FileText,
  History,
  Hospital,
  LayoutGrid,
  ListChecks,
  Network,
  PenLine,
  Plug,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Stethoscope,
  UserRound,
  Users,
} from 'lucide-react'
import type { ReactNode } from 'react'
import type { Role } from '@/lib/session'
import { routes } from '@/routes'

/**
 * Itens de navegação (docs/styleguide/03-componentes.md · 07). O menu encurta por perfil: o que a
 * pessoa não pode fazer some da vista, nunca fica cinza (RN-AUT-22).
 *
 * Três grupos: Westhetics (só root: clínicas, catálogo e contratos), Administração e Operação.
 * Root sem clínica ativa vê só o grupo Westhetics; com uma clínica ativa, age como gestora dela.
 */
export interface NavItem {
  label: string
  href: string
  icon: ReactNode
}

const stroke = { strokeWidth: 1.4 } as const

/**
 * Painel da Westhetics: o que vale para todas as clínicas. Rótulos de uma palavra, porque a sidebar
 * tem 200px e rótulo cortado ou abreviado não existe; o título da tela diz o nome inteiro.
 */
export const navWesthetics: NavItem[] = [
  { label: 'Clínicas', href: routes.admin.clinics, icon: <Hospital {...stroke} /> },
  { label: 'Redes', href: routes.admin.networks, icon: <Network {...stroke} /> },
  {
    label: 'Catálogo',
    href: routes.admin.catalogProcedures,
    icon: <BookOpenCheck {...stroke} />,
  },
  { label: 'Contratos', href: routes.admin.templates, icon: <ScrollText {...stroke} /> },
  {
    label: 'Questionário',
    href: routes.admin.questionnaire,
    icon: <ClipboardList {...stroke} />,
  },
  // Quem é root (RN-AUT-28 a 30): a equipe da Westhetics.
  { label: 'Equipe', href: routes.admin.team, icon: <ShieldCheck {...stroke} /> },
  // O que a Westhetics faz sozinha por todas as clínicas (RN-ADM-94 a 99): o monitoramento da equipe.
  { label: 'Monitoramento', href: routes.admin.operations, icon: <Activity {...stroke} /> },
]

const dashboard: NavItem = {
  label: 'Dashboard',
  href: routes.dashboard,
  icon: <LayoutGrid {...stroke} />,
}
const clinicProfile: NavItem = {
  label: 'Empresa',
  href: routes.admin.clinicProfile,
  icon: <Building2 {...stroke} />,
}
const users: NavItem = { label: 'Usuários', href: routes.admin.users, icon: <Users {...stroke} /> }
const practitioners: NavItem = {
  label: 'Prestadores',
  href: routes.admin.practitioners,
  icon: <Stethoscope {...stroke} />,
}
const procedures: NavItem = {
  label: 'Procedimentos',
  href: routes.admin.procedures,
  icon: <SlidersHorizontal {...stroke} />,
}
/** Os apps de integração, o MCP e a API (T53): só quem administra. */
const integrations: NavItem = {
  label: 'Integrações',
  href: routes.admin.integrations,
  icon: <Plug {...stroke} />,
}
const auditLog: NavItem = {
  label: 'Trilha',
  href: routes.admin.auditLog,
  icon: <History {...stroke} />,
}

/** Entra no grupo Administração só enquanto a implantação não termina (RN-ADM-80). */
export const navOnboarding: NavItem = {
  label: 'Implantação',
  href: routes.onboarding,
  icon: <ListChecks {...stroke} />,
}

/**
 * Administração da clínica ativa. Usuários, Integrações e a trilha de auditoria só para quem
 * administra (RN-ADM-16, RN-INT-01, RN-ADM-60).
 */
export function navAdminFor(role: Role): NavItem[] {
  if (role === 'manager' || role === 'root') {
    return [dashboard, clinicProfile, users, practitioners, procedures, integrations, auditLog]
  }
  return [dashboard, clinicProfile, practitioners, procedures]
}

export const navOperations: NavItem[] = [
  { label: 'Pacientes', href: routes.patients, icon: <UserRound {...stroke} /> },
  { label: 'Anamneses', href: routes.anamneses, icon: <ClipboardCheck {...stroke} /> },
  { label: 'Assinaturas', href: routes.signatures, icon: <PenLine {...stroke} /> },
  { label: 'Planos de Tratamento', href: routes.treatmentPlans, icon: <FileText {...stroke} /> },
]

/** Barra inferior do celular: as quatro rotas de uso diário. */
export const navMobile: NavItem[] = [
  { label: 'Hoje', href: routes.dashboard, icon: <CalendarDays {...stroke} /> },
  { label: 'Pacientes', href: routes.patients, icon: <UserRound {...stroke} /> },
  { label: 'Assinaturas', href: routes.signatures, icon: <PenLine {...stroke} /> },
  { label: 'Planos', href: routes.treatmentPlans, icon: <FileText {...stroke} /> },
]
